<script setup lang="ts">
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';

export type ProductPickingStatus = openapi._ProductOpenapi['picking_status'];
export type JobPickingStatus = NonNullable<
  openapi.JobOpenapi['picking_status']
>;
export type PickingStatus = ProductPickingStatus | JobPickingStatus;

interface StatusView {
  icon: string;
  colorClass: 'ok' | 'warning' | 'issue' | 'muted';
  label: string;
}

export interface Props {
  status?: PickingStatus;
}
const props = withDefaults(defineProps<Props>(), {
  status: 'unknown',
});

const statusViews: Record<PickingStatus, StatusView> = {
  ok: {
    icon: 'mdi-check-circle-outline',
    colorClass: 'ok',
    label: 'OK',
  },
  issue: {
    icon: 'mdi-alert-circle-outline',
    colorClass: 'issue',
    label: 'Issue',
  },
  qty_mismatch: {
    icon: 'mdi-close-box-outline',
    colorClass: 'issue',
    label: 'Qty mismatch',
  },
  warning: {
    icon: 'mdi-alert-outline',
    colorClass: 'warning',
    label: 'Warning',
  },
  skipped: {
    icon: 'mdi-cancel',
    colorClass: 'issue',
    label: 'Skipped',
  },
  no_data: {
    icon: 'mdi-circle-outline',
    colorClass: 'muted',
    label: 'No data',
  },
  unknown: {
    icon: 'mdi-help-circle-outline',
    colorClass: 'muted',
    label: 'Unknown',
  },
};

const statusView = computed(() => statusViews[props.status]);
</script>

<template>
  <v-chip
    size="x-small"
    variant="flat"
    :prepend-icon="statusView.icon"
    :class="[$style.pickingStatusChip, $style[statusView.colorClass]]"
    data-testid="pickingStatusChip"
  >
    {{ statusView.label }}
  </v-chip>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.pickingStatusChip {
  --v-chip-height: 20px;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
}

.ok {
  background-color: colors.$color-green-100;
  color: colors.$color-green-800;
}

.warning {
  background-color: colors.$color-orange-100;
  color: colors.$color-yellow-500;
}

.issue {
  background-color: colors.$color-red-100;
  color: colors.$color-red-800;
}

.muted {
  background-color: colors.$color-gray-200;
  color: colors.$color-text-secondary;
}
</style>
